import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Globe, InstagramLogo, MagnifyingGlass, MapPin, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import skyBackground from '../../../assets/sky-background.png';
import { Icon } from '../../../components/Icon';
import { Banner } from '../../../components/ui/banner';
import { BrandLogo } from '../../../components/ui/brand-logo';
import { buttonClassName } from '../../../components/ui/button';
import { Mascot } from '../../../components/ui/mascot';
import { StatusTag } from '../../../components/ui/status-tag';
import { fetchProfile, profileQueryOptions, saveProfile } from '../../../lib/business';
import { cx } from '../../../lib/cx';
import { requireAuditAccess } from '../../../lib/guards';
import { draftToProfileInput } from '../../../lib/onboarding';
import { useOnboardingStore } from '../../../lib/onboarding-store';

export const Route = createFileRoute('/subdomain/app/audit')({
  beforeLoad: ({ context }) => requireAuditAccess(context.queryClient),
  component: AuditPage,
});

const CHECKS: { label: string; icon: PhosphorIcon }[] = [
  { label: 'Website speed & SEO', icon: Globe },
  { label: 'Google Business Profile', icon: MapPin },
  { label: 'Instagram presence', icon: InstagramLogo },
  { label: 'Local search & reviews', icon: MagnifyingGlass },
];

// TODO(api): the audit runs server-side (n8n via QStash) but exposes no status yet, so this progress is
// a visual stand-in. Replace with real status once an endpoint exists.
const SECONDS_PER_CHECK = 6;

function useSimulatedScan(active: boolean) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) return;
    const total = CHECKS.length * SECONDS_PER_CHECK;
    const id = setInterval(() => setElapsed((e) => Math.min(e + 0.25, total)), 250);
    return () => clearInterval(id);
  }, [active]);
  const progress = elapsed / (CHECKS.length * SECONDS_PER_CHECK);
  return {
    progress,
    currentIndex: Math.min(CHECKS.length, Math.floor(progress * CHECKS.length)),
    finished: progress >= 1,
  };
}

type Phase = 'saving' | 'scanning' | 'error';

function AuditPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>('saving');
  const [businessName, setBusinessName] = useState(() => useOnboardingStore.getState().draft.businessName);

  // Signing up (email or social) lands here with the onboarding answers still in the store and no profile
  // yet, so this screen is where they are saved. If a profile already exists there is nothing to save.
  const run = useCallback(async () => {
    setPhase('saving');
    try {
      let profile = await fetchProfile();
      if (!profile) {
        await saveProfile(draftToProfileInput(useOnboardingStore.getState().draft));
        profile = await fetchProfile();
      }
      await queryClient.invalidateQueries({ queryKey: profileQueryOptions.queryKey });
      if (profile) setBusinessName(profile.businessName);
      useOnboardingStore.getState().reset();
      setPhase('scanning');
    } catch (e) {
      // The session ended while we were here (expired, or signed out in another tab): there is no account to save
      // to, so go back to creating one instead of showing a save error the user cannot act on.
      if ((e as { kind?: string }).kind === 'unauthorized') {
        await navigate({ to: '/subdomain/app/sign-up' });
        return;
      }
      setPhase('error');
    }
  }, [queryClient, navigate]);

  useEffect(() => {
    if (startedRef.current) return; // StrictMode runs effects twice in dev; saving is an upsert but queues an audit each time.
    startedRef.current = true;
    void run();
  }, [run]);

  const { progress, currentIndex, finished } = useSimulatedScan(phase === 'scanning');
  const percent = Math.round(progress * 100);

  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-brand-primary-light p-(--mbm-space-4)">
      <img src={skyBackground} alt="" aria-hidden className="absolute inset-0 size-full object-cover" />
      <span className="absolute left-(--mbm-space-8) top-(--mbm-space-8) hidden rounded-full bg-surface-card py-(--mbm-space-2) pl-(--mbm-space-3) pr-(--mbm-space-4) lg:inline-flex">
        <BrandLogo />
      </span>

      <section
        aria-live="polite"
        className="relative flex w-full max-w-(--mbm-layout-scan-card-width) flex-col items-center gap-(--mbm-space-5) rounded-(--mbm-radius-xl) border border-surface-card bg-surface-card p-(--mbm-space-8) text-center shadow-(--mbm-shadow-lg)"
      >
        <Mascot
          pose={finished ? 'cheer' : 'think'}
          className="h-(--mbm-size-mascot-height) w-(--mbm-size-mascot-width)"
        />

        {phase === 'error' ? (
          <div className="flex w-full flex-col gap-(--mbm-space-4) text-left">
            <Banner tone="error" title="We couldn’t save your business details">
              Your answers are safe on this device. Try again, or go back and check them.
            </Banner>
            <button type="button" onClick={() => void run()} className={buttonClassName({ fullWidth: true })}>
              Try again
            </button>
            <Link
              to="/subdomain/app/onboarding/industry"
              className={buttonClassName({ variant: 'secondary', fullWidth: true })}
            >
              Check my answers
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-(--mbm-space-2)">
              <h1 className="text-heading-h1 text-text-strong">
                {phase === 'saving'
                  ? 'Setting things up…'
                  : finished
                    ? 'Your audit is ready'
                    : `Scanning ${businessName || 'your business'}…`}
              </h1>
              <p className="text-body-medium text-text-secondary">
                {finished
                  ? 'Your health check is done. Let’s see how you’re doing.'
                  : 'Your free audit takes about 60 seconds. Grab a chai.'}
              </p>
            </div>

            <div className="flex w-full flex-col gap-(--mbm-space-1-5)">
              <div className="flex justify-between text-label-medium">
                <span className="text-text-secondary">Checking {CHECKS.length} things</span>
                <span className="text-brand-primary">{percent}%</span>
              </div>
              <div
                role="progressbar"
                aria-label="Audit progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                className="h-(--mbm-space-2-5) w-full overflow-hidden rounded-full bg-border-default"
              >
                <div
                  className="h-full rounded-full bg-brand-primary transition-[width] duration-(--mbm-duration-base)"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>

            <ul className="flex w-full flex-col gap-(--mbm-space-2)">
              {CHECKS.map((check, i) => {
                const state = i < currentIndex ? 'done' : i === currentIndex && !finished ? 'scanning' : 'next';
                return (
                  <li
                    key={check.label}
                    className={cx(
                      'flex items-center gap-(--mbm-space-3) rounded-(--mbm-radius-sm) px-(--mbm-space-4) py-(--mbm-space-3)',
                      state === 'scanning' ? 'bg-tag-blue-bg' : 'bg-surface-canvas',
                    )}
                  >
                    <span className={state === 'next' ? 'text-text-tertiary' : 'text-text-primary'}>
                      <Icon icon={check.icon} size="md" />
                    </span>
                    <span
                      className={cx(
                        'flex-1 text-left text-label-medium',
                        state === 'next' ? 'text-text-tertiary' : 'text-text-primary',
                      )}
                    >
                      {check.label}
                    </span>
                    {state === 'done' ? <StatusTag tone="green" label="Done" /> : null}
                    {state === 'scanning' ? (
                      <span className="text-label-small text-brand-primary">Scanning…</span>
                    ) : null}
                    {state === 'next' ? <span className="text-label-small text-text-tertiary">Up next</span> : null}
                  </li>
                );
              })}
            </ul>

            {finished ? (
              <Link to="/subdomain/app" className={buttonClassName({ fullWidth: true })}>
                Continue
              </Link>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
